import { CdkListboxModule, CdkOption } from '@angular/cdk/listbox';
import { ConnectedOverlayPositionChange, ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { CdkPortal } from '@angular/cdk/portal';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChild,
  contentChildren,
  effect,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  input,
  Output,
  signal,
  TemplateRef,
  untracked,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { combineLatest, delay, filter, map, of, Subject, switchMap, tap } from 'rxjs';

import { SelectItemsComponent } from './select-content.component';
import { SelectTriggerDirective } from './select-trigger.directive';
import { SelectService } from './select.service';
import { FormFieldControl } from '../form-field/form-field-control';
import { ErrorStateMatcher } from '../forms/error-options';
import { ErrorStateTracker } from '../forms/error-state-tracker';
import { IconDirective } from '../icon/icon.directive';

export interface SelectItem {
  value: string;
}

let selectId = 0;

@Component({
  selector: 'tt-select',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule, SelectTriggerDirective, CdkListboxModule, MatIconModule, IconDirective],
  providers: [
    SelectService,
    {
      provide: FormFieldControl,
      useExisting: SelectComponent,
    },
  ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
})
export class SelectComponent<T extends SelectItem> implements ControlValueAccessor, FormFieldControl {
  private service = inject(SelectService);
  private defaultErrorStateMatcher = inject(ErrorStateMatcher);
  private parentForm = inject(NgForm, { optional: true });
  private parentFormGroup = inject(FormGroupDirective, { optional: true });
  public ngControl = inject(NgControl, { optional: true, self: true });

  @HostBinding('class.tt-select') public hostClass = true;
  @HostBinding('class.tt-select--disabled') public get isDisabled() {
    return this.ngControl?.disabled;
  }

  public disableAutoWidth = input<boolean>(false);

  @Input()
  public set selected(value: string) {
    this.service.updateState({ value });
  }

  @Input()
  public set placeholder(placeholder: string) {
    this.service.updateState({ placeholder });
  }
  protected _placeholder = this.service.placeholder;

  @Input()
  public readonly = false;

  private _error = signal(false);
  @Input()
  public set error(value: boolean) {
    this._error.set(value);
  }

  public closeDelay = input<number>(100);

  @Output()
  public selectionChanged = new EventEmitter<T>();

  @Output()
  public openedChange = new EventEmitter<boolean>();

  @ContentChild(SelectItemsComponent)
  protected selectContent!: SelectItemsComponent;

  public isExpanded = this.service.isExpanded;
  protected _delayedExpanded = toSignal(
    toObservable(this.isExpanded).pipe(
      switchMap((expanded) => (!expanded ? of(expanded).pipe(delay(this.closeDelay())) : of(expanded))),
      takeUntilDestroyed(),
    ),
    { initialValue: false },
  );

  // eslint-disable-next-line
  @ContentChild(TemplateRef) public itemTemplate!: TemplateRef<any>;
  @ViewChild(CdkPortal) public selectOverlay!: CdkPortal;

  protected _positionChanges$ = new Subject<ConnectedOverlayPositionChange>();
  public errorState = computed(() => this.errorStateTracker.errorState());
  public triggerWidth = this.service.triggerWidth;
  public side = this.service.side;

  protected selectedLabel = computed(() => this.service.selectedOption()?.getLabel() ?? this._placeholder());
  protected items = contentChildren(CdkOption, { descendants: true });
  protected items$ = toObservable(this.items);
  protected itemsAndIndex$ = this.items$.pipe(
    takeUntilDestroyed(),
    map((options, index) => [options, index] as const),
  );

  private options = this.service.possibleOptions;
  private shouldEmitValueChange = this.service.shouldEmitChange;
  private errorStateTracker: ErrorStateTracker;
  private writeValue$ = new Subject<string>();

  constructor() {
    this.handleOptionChanges();
    this.handleInitialOptionSelect();

    this.service.updateState({ id: `tt-select-${selectId++}` });

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    /**
     * Listening to value changes in order to trigger forms api on change
     * ShouldEmitValueChange simply ensures we only propagate value change when a user makes a selection
     * we don't propagate changes made from outside the component (ex. patch value or initial value from form control)
     */
    toObservable(this.service.value).subscribe((value) => {
      if (this.shouldEmitValueChange()) {
        this._onChange(value ?? null);
        this.selectionChanged.emit(value);
      }
      this.close();
      this.service.updateState({ shouldEmitChange: true });
    });

    this._positionChanges$.subscribe((change) => this.service.setSide(change));

    this.errorStateTracker = new ErrorStateTracker(this.defaultErrorStateMatcher, this.ngControl, this.parentFormGroup, this.parentForm);

    effect(() => {
      if (this.ngControl) {
        const errorState = this.errorStateTracker.errorState();
        untracked(() => this._error.set(errorState));
      }
    });
  }

  /**
   * Toggles the expanded state of the select component.
   */
  public toggle(): void {
    if (this.isDisabled || this.readonly) {
      return;
    }
    if (this.isExpanded()) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Opens the select component.
   * If the component can be opened, it updates the state to expanded,
   * emits the openedChange event, and moves focus to the CDK list.
   */
  public open(): void {
    if (!this.canOpen()) return;

    this.service.setExpanded(true);
    this.openedChange.next(true);
    this._moveFocusToCDKList();
  }

  /**
   * Closes the select component.
   * If the component is currently expanded, it updates the state to not expanded,
   * emits the openedChange event, and focuses on the select trigger if available.
   */
  public close(): void {
    if (!this.isExpanded()) return;

    if (this.service.selectTrigger) {
      this.service.selectTrigger.focus();
    }

    this.service.setExpanded(false);
    this.openedChange.next(false);
    this._onTouched();
  }

  protected canOpen(): boolean {
    return !this.isExpanded() && !this.isDisabled && this.options()?.length > 0;
  }

  protected positions: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  public writeValue(value: string | null): void {
    this.writeValue$.next(value);
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: unknown) => void = () => {};
  public registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched = () => {};
  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  private _moveFocusToCDKList(): void {
    setTimeout(() => this.selectContent.focusList());
  }

  /**
   * Once writeValue is called and options are available we can handle setting the initial options
   */
  private handleInitialOptionSelect() {
    // Write value cannot be handled until options are available, so we wait until both are available with a combineLatest
    combineLatest([this.writeValue$, this.items$])
      .pipe(
        takeUntilDestroyed(),
        map(([writeValue], index) => ({ writeValue, index })),
        tap(({ writeValue }) => {
          this.service.setInitialSelectedOption(writeValue);
        }),
        filter(({ index }) => index === 0),
      )
      .subscribe(() => {
        // Mark control as pristine when handling the initial state
        this.ngControl?.control?.markAsPristine();
      });
  }

  /**
   * When options change, our current selected options may become invalid
   * Here we will automatically update our current selected options so that they are always inline with the possibleOptions
   */
  private handleOptionChanges() {
    this.itemsAndIndex$.subscribe(([options, index]) => {
      if (index > 0) {
        this.service.setPossibleOptions([...options]);
      } else {
        this.service.updateState({ possibleOptions: [...options] });
      }
    });
  }
}
